import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { useEnv } from '../../context/EnvContext';
import { useAppEnv } from '../../api/useAppEnv';
import { useActiveTenantEditable } from '../../context/useActiveTenantEditable';
import { READONLY_INTERACTIVE_ATTR } from '../../context/ReadOnlyGuard';
import { colors } from '../../theme/tokens';

/**
 * WU11 — Sticky banner shown on every page when the user is viewing a
 * read-only env (i.e. anything other than the editable env). Designed so
 * mutations the user attempts in spite of disabled inputs (browser tools,
 * mid-flight requests, etc.) still surface the "you're not in dev" context.
 *
 * WAVE G — two read-only flavors:
 *   1. GLOBAL read-only — the env itself is read-only (browsing a remote env,
 *      or a non-editable deployment). Message names current/editable env.
 *   2. TENANT read-only — the env IS editable, but the active TENANT is
 *      authored on a different home env. Distinct copy + a link to switch
 *      to the tenant's home env in the browser's current view.
 *
 * PORTAL = VIEWPORT: the bundle is deployed once and served from every env,
 * and `setCurrentEnv(homeEnv)` points BOTH reads and writes at that env
 * (axiosClient routes mutations browser-direct; the target's write fence is
 * authoritative). The Switch button therefore grants real editing when the
 * target is the tenant's home.
 *
 * The tenant variant only appears when the per-tenant backend is on AND the
 * tenant is homed elsewhere; with the backend off it never fires (editableHere
 * tracks env-level), so flag-off behavior is byte-identical.
 *
 * Accessibility:
 *   - `role="status"` + `aria-live="polite"` so screen readers announce
 *     when the user navigates to a page with read-only state.
 *   - NOT dismissible. The state is environmental, not a one-off notice.
 */
const ReadOnlyBanner: React.FC = () => {
  const { editableEnv, setCurrentEnv, availableEnvs } = useEnv();
  const appEnv = useAppEnv();
  const perTenantOn = !!appEnv?.perTenantEditingEnvEnabled;
  const { editable, tenantCode, editableHere, homeEnv, migratingTo, currentEnv } =
    useActiveTenantEditable();

  if (editable) return null;

  // Tenant variant: a tenant is in context and it is homed on a DIFFERENT env
  // than the one the UI is pointed at. Portal = viewport: switching the env
  // dropdown to the tenant's home makes it genuinely editable (writes go
  // browser-direct to the home env), so the banner offers that switch.
  // Requires the per-tenant backend: with the flag OFF, homes don't exist and
  // the legacy GLOBAL copy must render unchanged (flag-off is byte-identical).
  const isTenantVariant =
    perTenantOn && !!tenantCode && !editableHere && currentEnv !== 'prod';

  // The home env is only offered as a Switch target when it's in the browsable
  // list (defensive — a home we can't navigate to shouldn't render a dead button).
  const canSwitchHome =
    isTenantVariant && !!homeEnv && availableEnvs.some((e) => e.name === homeEnv);

  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="read-only-banner"
      data-variant={isTenantVariant ? 'tenant' : 'global'}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 4,
        py: 1.5,
        backgroundColor: colors.warningBgStrong,
        borderBottom: `1px solid ${colors.warningBorder}`,
      }}
    >
      <LockOutlinedIcon sx={{ color: colors.warningIcon, width: 20, height: 20 }} />
      <Typography
        sx={{
          fontFamily: 'Manrope, sans-serif',
          fontSize: 13,
          fontWeight: 600,
          color: colors.warningText,
          flexGrow: 1,
        }}
      >
        {isTenantVariant ? (
          <>
            This tenant is authored on <strong>{homeEnv}</strong>. You are viewing{' '}
            <strong>{currentEnv}</strong> — edits are read-only here.
            {' '}Switch to <strong>{homeEnv}</strong> to edit.
            {migratingTo && (
              <>
                {' '}
                A home handoff to <strong>{migratingTo}</strong> is in progress.
              </>
            )}
          </>
        ) : (
          <>
            {currentEnv === 'prod' ? (
              <>
                Read-only view — <strong>prod</strong> is never editable from the
                portal. Changes reach prod through promotion only.
              </>
            ) : (
              <>
                Read-only view — you are connected to <strong>{currentEnv}</strong>.
                Mutations are only allowed in <strong>{editableEnv}</strong>.
              </>
            )}
          </>
        )}
      </Typography>

      {canSwitchHome && (
        // Marked interactive so ReadOnlyGuard never freezes this escape hatch.
        // Switching points the viewport at {homeEnv}; writes then go
        // browser-direct to that env, so editing genuinely works after the switch.
        <span {...{ [READONLY_INTERACTIVE_ATTR]: 'true' }}>
          <Button
            size="small"
            variant="outlined"
            data-testid="read-only-banner-switch"
            onClick={() => setCurrentEnv(homeEnv as string)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 12,
              borderColor: colors.warningIcon,
              color: colors.warningText,
              '&:hover': { borderColor: colors.warningText, backgroundColor: 'transparent' },
            }}
          >
            Switch to {homeEnv} to edit
          </Button>
        </span>
      )}
    </Box>
  );
};

export default ReadOnlyBanner;
